import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { type z } from 'zod';

import { SecaoFormulario } from '@/ui/patterns/secao-formulario';
import { Button } from '@/ui/primitives/button';
import { Campo, Input } from '@/ui/primitives/input';
import { type Contador, contadorSchema, type EmpresaDetalhe } from '@fiscoai/contracts';

import { useDefinirContador } from './api';
import { gravar, nulo } from './formulario';

const VAZIO: Contador = {
  nome: '',
  crc: null,
  documento: null,
  escritorio: null,
  email: null,
  telefone: null,
};

export function AbaContador({
  empresa,
  podeEditar,
}: {
  empresa: EmpresaDetalhe;
  podeEditar: boolean;
}) {
  const definir = useDefinirContador(empresa.id);
  const form = useForm<z.input<typeof contadorSchema>, unknown, Contador>({
    resolver: zodResolver(contadorSchema),
    values: empresa.contador ?? VAZIO,
  });
  const erros = form.formState.errors;
  const enviar = form.handleSubmit((dados) =>
    gravar(() => definir.mutateAsync(dados), 'Contador salvo'),
  );
  const remover = () => gravar(() => definir.mutateAsync(null), 'Contador removido');

  return (
    <SecaoFormulario
      titulo="Contador responsável"
      descricao="Quem recebe o pacote do mês e responde pela escrituração."
      aoEnviar={() => void enviar()}
      acoes={
        podeEditar && (
          <AcoesContador
            temContador={empresa.contador !== null}
            mudou={form.formState.isDirty}
            salvando={definir.isPending}
            aoRemover={() => void remover()}
          />
        )
      }
    >
      <fieldset disabled={!podeEditar} className="grid gap-4 sm:grid-cols-2">
        <legend className="sr-only">Contador</legend>
        <Campo rotulo="Nome" erro={erros.nome?.message}>
          {(a) => <Input {...a} {...form.register('nome')} />}
        </Campo>
        <Campo rotulo="CRC" erro={erros.crc?.message}>
          {(a) => <Input {...a} {...form.register('crc', nulo)} />}
        </Campo>
        <Campo rotulo="Escritório" erro={erros.escritorio?.message}>
          {(a) => <Input {...a} {...form.register('escritorio', nulo)} />}
        </Campo>
        <Campo rotulo="CPF ou CNPJ" erro={erros.documento?.message}>
          {(a) => <Input {...a} className="font-mono" {...form.register('documento', nulo)} />}
        </Campo>
        <Campo rotulo="E-mail" erro={erros.email?.message}>
          {(a) => <Input type="email" {...a} {...form.register('email', nulo)} />}
        </Campo>
        <Campo rotulo="Telefone" erro={erros.telefone?.message}>
          {(a) => <Input type="tel" {...a} {...form.register('telefone', nulo)} />}
        </Campo>
      </fieldset>
    </SecaoFormulario>
  );
}

interface AcoesProps {
  temContador: boolean;
  mudou: boolean;
  salvando: boolean;
  aoRemover: () => void;
}

function AcoesContador({ temContador, mudou, salvando, aoRemover }: AcoesProps) {
  return (
    <>
      {temContador && (
        <Button variante="fantasma" onClick={aoRemover}>
          Remover
        </Button>
      )}
      {mudou && (
        <Button type="submit" variante="primario" carregando={salvando}>
          Salvar
        </Button>
      )}
    </>
  );
}
